import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { z } from "zod";

import NoticePreviewContainer from "components/record_details/NoticePreview";
import Drawer from "components/ui/drawer/Drawer";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useURLState } from "lib/hooks/useURLState";
import { useTracking } from "lib/tracking";
import { LoggedEntityType } from "lib/types/graphQLEnums";

// The open record is mirrored in this URL param so browser back/forward and deep
// links restore the slide-out (e.g. open preview -> click a buyer -> back reopens it).
// Namespaced to notices so a sibling preview (e.g. documents) can use its own param.
const PREVIEW_PARAM = "noticePreview";
const previewGuidSchema = z.string().min(1).optional();

type CustomViewerConfig = {
  renderContent: (recordGuid: string) => React.ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RecordViewerEventData = Record<string, any>;

type RecordViewer = {
  viewRecord: (
    recordGuid: string,
    trackingData: RecordViewerEventData,
    customConfig?: CustomViewerConfig,
  ) => void;
  closeRecord: () => void;
  recordGuid: string | undefined;
};

const RecordViewerContext = React.createContext<RecordViewer | undefined>(undefined);

type RecordViewerProviderProps = {
  children: React.ReactNode;
};

export function RecordViewerProvider({ children }: RecordViewerProviderProps): JSX.Element {
  // The `preview` URL param is the source of truth for which record is open, so
  // navigating away and pressing back restores the slide-out. `eventData` and
  // `customConfig` can't live in the URL, so they stay in local state — set when a
  // record is opened in-session, and absent on a back/deep-link restore (which then
  // falls back to the default NoticePreview rendering).
  const [previewGuid, setPreviewGuid] = useURLState<typeof previewGuidSchema>(
    PREVIEW_PARAM,
    undefined,
    previewGuidSchema,
  );

  // Local mirror of the open state, kept so the drawer can play its close animation
  // before the content unmounts (clearing `recordGuid` is delayed; `previewGuid`
  // clears immediately).
  const [isOpen, setIsOpen] = useState(!!previewGuid);
  const [recordGuid, setRecordGuid] = useState<string | undefined>(previewGuid);
  const [eventData, setEventData] = useState<RecordViewerEventData | undefined>(undefined);
  const [customConfig, setCustomConfig] = useState<CustomViewerConfig>();
  // Keyed on the URL param, so a drawer restored by a deep link or browser back is counted too
  useLogEntityViewOnOpen(LoggedEntityType.Notice, previewGuid);

  useEffect(() => {
    if (previewGuid) {
      setRecordGuid(previewGuid);
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
    const animationWait = setTimeout(() => {
      setRecordGuid(undefined);
      setEventData(undefined);
      setCustomConfig(undefined);
    }, 300);
    return () => clearTimeout(animationWait);
  }, [previewGuid]);

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setPreviewGuid(undefined);
    }
  };

  const context = useMemo(
    () => ({
      viewRecord: (
        guid: string,
        trackingData: RecordViewerEventData,
        config?: CustomViewerConfig,
      ) => {
        setEventData(trackingData);
        setCustomConfig(config);
        setPreviewGuid(guid);
      },
      closeRecord: () => setPreviewGuid(undefined),
      recordGuid: previewGuid,
    }),
    [previewGuid, setPreviewGuid],
  );

  return (
    <RecordViewerContext.Provider value={context}>
      {children}
      {recordGuid && (
        <Drawer open={isOpen} onOpenChange={onOpenChange} width={1200}>
          {customConfig ? (
            customConfig.renderContent(recordGuid)
          ) : (
            <NoticePreviewContainer guid={recordGuid} eventData={eventData} />
          )}
        </Drawer>
      )}
    </RecordViewerContext.Provider>
  );
}

export function useRecordViewer(customConfig?: CustomViewerConfig) {
  const { trackingData } = useTracking();
  const context = useContext(RecordViewerContext);
  if (context === undefined) {
    throw new Error("useRecordViewer must be used within a RecordViewerProvider");
  }
  const viewRecord = useCallback(
    (recordGuid: string, eventData: { "Context source": string }) => {
      return context.viewRecord(recordGuid, { ...trackingData, ...eventData }, customConfig);
    },
    [context, trackingData, customConfig],
  );

  const closeRecord = useCallback(() => context.closeRecord(), [context]);

  return { ...context, viewRecord, closeRecord };
}
